import "./styles.css";
import { useRef, useState, useEffect } from "react";
import { IoClose } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import { TabularCard } from "../TabularCard/TabularCard";
import { PercentageCircles } from "../PercentageCircles/PercentageCircles";
import { TabularInfoAiBar } from "./Utilities";
import { useConfig } from "../../context/ConfigContext";
import { useUID } from "../../context/UIDContext";
import { useAmplitude } from "../../context/AmplitudeContext";
// import useSwipe from "../../hooks/useSwipe";
import { convertStrToSlug } from "../../services/utilitiesService";
import {
  MdInfoOutline,
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
} from "react-icons/md";
import { GeneralDescription } from "../ResultDescription/GeneralDescription/GeneralDescription";
import { Wrinklesv2Description } from "../ResultDescription/Wrinklesv2Description/Wrinklesv2Description";
import { useTracker } from "../../context/TrackerContext";

export const Results = ({ results, onProductClick, onExitClick }) => {
  const { t } = useTranslation(["aiResults", "customAiInfo"]);
  const imgRef = useRef(null);
  const detailTextRef = useRef(null);
  const [selectedResult, setSelectedResult] = useState(Object.keys(results)[0]);
  const { uid } = useUID();
  const trackEvent = useAmplitude();
  const { itrack } = useTracker();
  const { configData } = useConfig();

  useEffect(() => {
    // Track user visits Results Page
    itrack({
      eventName: "visited_results_page",
      trackerId: uid,
    });
    trackEvent("webapp_user_visited_results_page", uid, "results");
    if (!configData.recommendationEnabled) {
      trackEvent("webapp_user_visited_last_page", uid, "results");
    }
  }, [uid, trackEvent, itrack, configData.recommendationEnabled]);

  const loadShortInfo = () => {
    switch (selectedResult) {
      case "Hair Gloss":
        return (
          <h4 className="result-decision">
            {results[selectedResult].percentages &&
            Object.keys(results[selectedResult].percentages).length > 0
              ? `${results[selectedResult].percentages.Glossy}% ${t("Glossy")} `
              : t("No hair")}
          </h4>
        );
      case "Hair Loss":
        return (
          <h4 className="result-decision">
            {t(`hair-loss-${convertStrToSlug(results[selectedResult].value)}`)}
          </h4>
        );
      case "Skin Type":
        return (
          <h4 className="result-decision">
            {configData?.ResultsFlag?.showSkinTypeDecision
              ? t(results[selectedResult].value)
              : null}
          </h4>
        );
      default:
        return (
          <h4 className="result-decision">
            {t(results[selectedResult].value)}
          </h4>
        );
    }
  };

  const loadResultCategories = () => {
    const resultNames = [];
    for (const key in results) {
      resultNames.push(key);
    }

    return resultNames.map((resultName, index) => (
      <button
        className={`result-button ${
          resultName === selectedResult ? "selected" : ""
        }`}
        key={index}
        id={convertStrToSlug(resultName)}
        onClick={() => setSelectedResult(resultName)}
      >
        {t(resultName)}
      </button>
    ));
  };

  const loadNextResultCategoryBySwipe = (direction) => {
    const resultArr = Object.keys(results);
    let newSelectedResult;

    if (direction === "left") {
      newSelectedResult =
        resultArr.indexOf(selectedResult) === resultArr.length - 1
          ? resultArr[0] // When the last image is Swiped on the left, load the first image
          : resultArr[resultArr.indexOf(selectedResult) + 1];
    } else if (direction === "right") {
      newSelectedResult =
        resultArr.indexOf(selectedResult) === 0
          ? resultArr[resultArr.length - 1] // When the first image is swiped on the right, load the last image
          : resultArr[resultArr.indexOf(selectedResult) - 1];
    } else {
      newSelectedResult = selectedResult;
    }

    // Update the state with the new result
    setSelectedResult(newSelectedResult);

    // Use the new result to scroll the corresponding button into view
    const buttonId = convertStrToSlug(newSelectedResult);
    const buttonElement = document.getElementById(buttonId);

    if (buttonElement) {
      buttonElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  };

  const scrollToDetailText = () => {
    if (detailTextRef.current) {
      detailTextRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // const swipeHandlers = useSwipe(loadNextResultCategoryBySwipe);

  return (
    <div className="result">
      <div className="result-short-info">
        <img
          ref={imgRef}
          className="background-image"
          src={`data:image/png;base64, ${results[selectedResult].image}`}
          alt="Result"
        />
        <div className="results-close-wrapper">
          <IoClose
            className="results-close-icon"
            onClick={() => {
              itrack({
                eventName: "pressed_results_close_icon",
                trackerId: uid,
              });
              trackEvent(
                "webapp_user_pressed_results_close_icon",
                uid,
                "results",
              );
              onExitClick();
            }}
          />
        </div>
        <div className="result-info-container">
          <MdKeyboardArrowLeft
            className="result-info-arrow"
            onClick={() => loadNextResultCategoryBySwipe("right")}
          />
          <div className="results-info">
            <div className="result-type" onClick={scrollToDetailText}>
              <p>{t(selectedResult)}</p>
              <MdInfoOutline />
            </div>
            {loadShortInfo()}
          </div>
          <MdKeyboardArrowRight
            className="result-info-arrow"
            onClick={() => loadNextResultCategoryBySwipe("left")}
          />
        </div>
        {results[selectedResult].percentages &&
          Object.keys(results[selectedResult].percentages).length > 0 && (
            <PercentageCircles
              percentages={results[selectedResult].percentages}
            />
          )}
        <div className="result-types">
          <div
            className="result-types-button-wrapper"
            onTouchStart={(event) => event.stopPropagation()}
          >
            {loadResultCategories()}
          </div>
        </div>
        <div className="result-bottom-buttons-container">
          {configData.recommendationEnabled && (
            <button
              className="recommend-button"
              onClick={() => {
                itrack({
                  eventName: "pressed_show_products",
                  trackerId: uid,
                });
                trackEvent("webapp_user_press_show_products", uid);
                onProductClick();
              }}
            >
              {t("Product Recommendation")}
            </button>
          )}
        </div>
        <div className="text-info-background" />
      </div>
      <div ref={detailTextRef} className="result-detail-info">
        {results[selectedResult].percentages ? (
          <TabularCard tabNames={TabularInfoAiBar[selectedResult]} />
        ) : selectedResult === "Wrinkles v2" ? (
          <Wrinklesv2Description aiValue={results[selectedResult].value} />
        ) : (
          <GeneralDescription
            selectedResult={selectedResult}
            aiValue={results[selectedResult].value}
          />
        )}
      </div>
    </div>
  );
};
